import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  InputAdornment, 
  Card, 
  CardContent, 
  CardActionArea, 
  CircularProgress, 
  Alert,
  Button,
  IconButton
} from '@mui/material';
import { Search, ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFormsByCategory } from '../hooks/useForm';
import { useCategoryById } from '../hooks/useCategories';
import { useLanguage } from '../contexts/LanguageContext';
import { getLocalizedName as getFormLocalizedName, getLocalizedShortDescription } from '../models/FormItem';
import { getLocalizedName as getDocumentLocalizedName } from '../models/Category';

export const DocumentPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');

  const { data: document, isLoading: documentLoading } = useCategoryById(categoryId || '');
  const { data: forms, isLoading: formsLoading, error } = useFormsByCategory(categoryId || '');

  // Scroll to top on mount to prevent auto-scroll
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredForms = forms?.filter((form: any) => {
    const name = getFormLocalizedName(form, currentLanguage);
    const desc = getLocalizedShortDescription(form, currentLanguage);
    const query = searchQuery.toLowerCase();
    return (name && String(name).toLowerCase().includes(query)) || 
           (desc && String(desc).toLowerCase().includes(query));
  });

  const isLoading = documentLoading || formsLoading;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mb: 2 }}>
            <ArrowBack />
          </IconButton>
          {document && (
            <Typography variant="h3" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
              {getDocumentLocalizedName(document, currentLanguage)}
            </Typography>
          )}
        </Box>

        <TextField
          fullWidth
          placeholder={t('search_placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus={false}
          InputProps={{ 
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ) 
          }}
          sx={{ mb: 4 }}
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{t('error_occurred')}</Alert>}
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredForms && filteredForms.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredForms.map((form: any) => (
              <Card 
                key={form.id}
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  }
                }}
              >
                <CardActionArea onClick={() => navigate(`/document/${categoryId}/${form.id}`)}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography 
                      variant="h6" 
                      fontWeight="600"
                      sx={{ mb: 1, fontSize: '1rem' }}
                    >
                      {getFormLocalizedName(form, currentLanguage)}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mb: 2, fontSize: '0.875rem' }}
                    >
                      {getLocalizedShortDescription(form, currentLanguage)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {t('no_forms_found')}
            </Typography>
          </Box>
        )}
      </Container>
  );
};
